import {
  faCalendarAlt,
  faCheckCircle,
  faDownload,
  faEdit,
  faExclamationTriangle,
  faEye,
  faInfoCircle,
  faSpinner,
  faTrash,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Badge, Button, Card, Grid, Loader, Select, Stack, Table, TextInput } from "@mantine/core";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import BankDetailsApi, { IAuditLogFilters, IBankDetailsAuditEntry, IPersonSummary } from "../../../data/api/bankDetailsApi";
import { IState } from "../../../data/reducers/state";
import { IPerson } from "../../../types/persons/person";
import { Privilege } from "../../../types/persons/privilege";

// Remove the old interface as we'll use the imported one

interface BankDetailsAuditLogProps {
  // Redux props
  self: IPerson;
  privilege: Privilege;
  // Association context
  associationId?: string;
}

const BankDetailsAuditLog: React.FunctionComponent<BankDetailsAuditLogProps> = ({
  self,
  privilege,
  associationId,
}) => {
  const [auditEntries, setAuditEntries] = useState<IBankDetailsAuditEntry[]>([]);
  const [personLookup, setPersonLookup] = useState<Map<string, IPersonSummary>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<IAuditLogFilters>({
    personId: '',
    accessedBy: '',
    limit: 50,
    associationId,
  });

  const canViewAudit = self?.admin === true;

  useEffect(() => {
    if (canViewAudit) {
      loadAuditLogs();
    }
  }, [canViewAudit]);

  // Create person lookup map for efficient name resolution
  const createPersonLookup = (persons: IPersonSummary[]): Map<string, IPersonSummary> => {
    const lookup = new Map();
    persons.forEach(person => {
      lookup.set(person._id, person);
      lookup.set(person.id, person); // Also map by id field for compatibility
    });
    return lookup;
  };

  const loadAuditLogs = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await BankDetailsApi.getAuditLog(associationId || '', filters);

      if (result.success) {
        setAuditEntries(result.data);
        setPersonLookup(createPersonLookup(result.persons));
      } else {
        setError(result.error || 'Failed to fetch audit log');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    loadAuditLogs();
  };

  const handleClearFilters = () => {
    setFilters({
      personId: '',
      accessedBy: '',
      limit: 50,
      associationId,
    });
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getAccessTypeIcon = (accessType: string) => {
    switch (accessType) {
      case 'view':
        return faEye;
      case 'update':
        return faEdit;
      case 'delete':
        return faTrash;
      case 'export':
        return faDownload;
      default:
        return faInfoCircle;
    }
  };

  const getAccessTypeBadge = (accessType: string) => {
    const colors = {
      view: 'info',
      update: 'warning',
      delete: 'danger',
      export: 'success',
    };

    return (
      <Badge color={colors[accessType] || 'secondary'}>
        <FontAwesomeIcon icon={getAccessTypeIcon(accessType)} className="me-1" />
        {accessType.toUpperCase()}
      </Badge>
    );
  };

  // Render person name with profile link
  const renderPersonCell = (personId: string) => {
    const person = personLookup.get(personId);

    if (person) {
      // Use preferredName if available and not empty, otherwise firstName
      const displayName = (person.preferredName && person.preferredName.trim())
        ? person.preferredName
        : person.firstName;
      const fullDisplayName = `${displayName} ${person.lastName}`;
      const profileUrl = `/u/${person.externalIdentifier}`;

      return (
        <div className="d-flex align-items-center">
          <FontAwesomeIcon icon={faUser} className="text-muted me-2" />
          <div>
            <div className="fw-bold">
              <Link
                to={profileUrl}
                className="text-decoration-none"
                title={`View profile for ${fullDisplayName}`}
              >
                {fullDisplayName}
              </Link>
            </div>
            <div className="text-muted small">
              ID: {person.externalIdentifier}
            </div>
          </div>
        </div>
      );
    }

    // Fallback to ID if person not found
    return (
      <div className="d-flex align-items-center">
        <FontAwesomeIcon icon={faUser} className="text-muted me-2" />
        <div>
          <div className="fw-bold text-muted">Unknown</div>
          <div className="text-muted small" title="Person data not available">
            {personId}
          </div>
        </div>
      </div>
    );
  };

  if (!canViewAudit) {
    return (
      <Alert color="red">
        <h5>Access Denied</h5>
        <p>Only system administrators can view the bank details audit log.</p>
      </Alert>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">Bank Details Audit Log</h4>
          <p className="text-muted mb-0">
            Track all bank detail access, modifications, and operations
            {associationId && " for this association"}
          </p>
        </div>
        <Button color="blue" onClick={loadAuditLogs} disabled={loading}>
          {loading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
              Loading...
            </>
          ) : (
            'Refresh'
          )}
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <Card.Section withBorder p="md">
          <h6 className="mb-0">Filters</h6>
        </Card.Section>
        <div style={{ padding: "1rem" }}>
          <Stack gap="md">
            <Grid>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Stack gap="sm">
                  <label className="fw-500" htmlFor="personIdFilter">Person ID</label>
                  <TextInput
                    type="text"
                    id="personIdFilter"
                    placeholder="Filter by person ID"
                    value={filters.personId}
                    onChange={(e) => handleFilterChange('personId', e.target.value)}
                  />
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Stack gap="sm">
                  <label className="fw-500" htmlFor="accessedByFilter">Accessed By (User ID)</label>
                  <TextInput
                    type="text"
                    id="accessedByFilter"
                    placeholder="Filter by accessor ID"
                    value={filters.accessedBy}
                    onChange={(e) => handleFilterChange('accessedBy', e.target.value)}
                  />
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 2 }}>
                <Stack gap="sm">
                  <label className="fw-500" htmlFor="limitFilter">Limit</label>
                  <Select
                    id="limitFilter"
                    value={filters.limit?.toString()}
                    onChange={(value) => handleFilterChange('limit', parseInt(value))}
                    data={[
                      { value: "25", label: "25" },
                      { value: "50", label: "50" },
                      { value: "100", label: "100" }
                    ]}
                  />
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 2 }} className="d-flex align-items-end">
                <Stack gap="sm" className="w-100">
                  <div className="d-flex gap-2">
                    <Button color="blue" size="sm" onClick={handleApplyFilters}>
                      Apply
                    </Button>
                    <Button color="gray" size="sm" onClick={handleClearFilters}>
                      Clear
                    </Button>
                  </div>
                </Stack>
              </Grid.Col>
            </Grid>
          </Stack>
        </div>
      </Card>

      {/* Error State */}
      {error && (
        <Alert color="red" className="mb-4">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {error}
        </Alert>
      )}

      {/* Audit Log Table */}
      <Card>
        <Card.Section withBorder p="md">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">Audit Entries ({auditEntries.length})</h6>
            {loading && <Loader size="sm" />}
          </div>
        </Card.Section>
        <div style={{ padding: "1rem" }} className="p-0">
          {auditEntries.length > 0 ? (
            <div className="table-responsive">
              <Table className="mb-0">
                <Table.Thead >
                  <tr>
                    <th>Timestamp</th>
                    <th>Action</th>
                    <th>Person</th>
                    <th>Accessed By</th>
                    <th>Reason</th>
                    <th>Status</th>
                  </tr>
                </Table.Thead>
                <tbody>
                  {auditEntries.map((entry) => (
                    <tr key={entry._id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon icon={faCalendarAlt} className="text-muted me-2" />
                          <div>
                            <div className="fw-bold">
                              {formatTimestamp(entry.timestamp)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {getAccessTypeBadge(entry.accessType)}
                      </td>
                      <td>
                        {renderPersonCell(entry.personId)}
                      </td>
                      <td>
                        {renderPersonCell(entry.accessedBy)}
                      </td>
                      <td>
                        <div className="text-truncate" style={{ maxWidth: '200px' }} title={entry.reason || ''}>
                          {entry.reason || '-'}
                        </div>
                        {entry.context && (
                          <div className="text-muted small">
                            {entry.context.endpoint && `Endpoint: ${entry.context.endpoint}`}
                          </div>
                        )}
                      </td>
                      <td>
                        {entry.success ? (
                          <Badge color="green">
                            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                            Success
                          </Badge>
                        ) : (
                          <div>
                            <Badge color="red">
                              <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                              Failed
                            </Badge>
                            {entry.error && (
                              <div className="text-danger small mt-1" title={entry.error}>
                                {entry.error.length > 50 ? `${entry.error.substring(0, 50)}...` : entry.error}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : !loading ? (
            <div className="text-center py-4 text-muted">
              <FontAwesomeIcon icon={faInfoCircle} size="2x" className="mb-3" />
              <h6>No audit entries found</h6>
              <p>Try adjusting your filters or check back later.</p>
            </div>
          ) : null}
        </div>
      </Card>

      {/* Info Section */}
      <Alert color="blue" className="mt-4">
        <h6>
          <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
          About Bank Details Audit Log
        </h6>
        <ul className="mb-0">
          <li><strong>View:</strong> Records when bank details are accessed or retrieved</li>
          <li><strong>Update:</strong> Records when bank details are created or modified</li>
          <li><strong>Delete:</strong> Records when bank details are removed</li>
          <li><strong>Export:</strong> Records bulk operations like CSV imports/exports</li>
          <li><strong>Security:</strong> All access is logged with timestamp, user, and reason</li>
          <li><strong>Association Scope:</strong> {associationId ? "Shows only bank detail access for members of this association" : "Shows all bank detail access across all associations"}</li>
          <li><strong>Retention:</strong> Audit logs are permanently retained for compliance</li>
        </ul>
      </Alert>
    </div>
  );
};

const mapStateToProps = (state: IState) => ({
  self: state.persons.self && state.persons.map[state.persons.self],
  privilege: state.auth.privilege,
});

export default connect(mapStateToProps)(BankDetailsAuditLog);
